import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { createEmptyWallet } from '../data/gameSystems';

const STORAGE_KEY = 'mywallets_data';

const initialState = {
  language: 'it',
  theme: 'parchment',
  customTheme: null,
  characters: [],
  selectedCharacterId: null,
  currentView: 'home', // 'home' or 'wallet'
};

const loadFromStorage = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      // Migrate old goals to have savedAmount
      if (parsed.characters) {
        parsed.characters = parsed.characters.map(char => ({
          ...char,
          goals: (char.goals || []).map(goal => ({
            ...goal,
            savedAmount: goal.savedAmount || 0
          }))
        }));
      }
      return { ...initialState, ...parsed, currentView: 'home' };
    }
  } catch (e) {
    console.error('Error loading from storage:', e);
  }
  return initialState;
};

const saveToStorage = (state) => {
  try {
    // Don't save currentView to storage
    const { currentView, ...stateToSave } = state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  } catch (e) {
    console.error('Error saving to storage:', e);
  }
};

const appReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LANGUAGE':
      return { ...state, language: action.payload };
    
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    
    case 'SET_CUSTOM_THEME':
      return { ...state, customTheme: action.payload };
    
    case 'SET_VIEW':
      return { ...state, currentView: action.payload };
    
    case 'CREATE_CHARACTER': {
      const newCharacter = {
        id: Date.now().toString(),
        name: action.payload.name,
        systemId: action.payload.systemId,
        wallet: createEmptyWallet(action.payload.systemId),
        transactions: [],
        goals: [],
        createdAt: new Date().toISOString(),
      };
      return {
        ...state,
        characters: [...state.characters, newCharacter],
        selectedCharacterId: newCharacter.id,
      };
    }
    
    case 'UPDATE_CHARACTER': {
      return {
        ...state,
        characters: state.characters.map(char =>
          char.id === action.payload.id
            ? { ...char, ...action.payload.updates }
            : char
        ),
      };
    }
    
    case 'DELETE_CHARACTER': {
      const newCharacters = state.characters.filter(c => c.id !== action.payload);
      return {
        ...state,
        characters: newCharacters,
        selectedCharacterId: newCharacters.length > 0 ? newCharacters[0].id : null,
        currentView: 'home',
      };
    }
    
    case 'SELECT_CHARACTER':
      return { ...state, selectedCharacterId: action.payload };
    
    case 'UPDATE_WALLET': {
      return {
        ...state,
        characters: state.characters.map(char =>
          char.id === action.payload.characterId
            ? { ...char, wallet: action.payload.wallet }
            : char
        ),
      };
    }
    
    case 'EMPTY_WALLET': {
      return {
        ...state,
        characters: state.characters.map(char =>
          char.id === action.payload.characterId
            ? { ...char, wallet: createEmptyWallet(char.systemId) }
            : char
        ),
      };
    }
    
    case 'ADD_TRANSACTION': {
      const transaction = {
        id: Date.now().toString(),
        ...action.payload.transaction,
        timestamp: new Date().toISOString(),
      };
      return {
        ...state,
        characters: state.characters.map(char =>
          char.id === action.payload.characterId
            ? { ...char, transactions: [transaction, ...char.transactions] }
            : char
        ),
      };
    }
    
    case 'CREATE_GOAL': {
      const goal = {
        id: Date.now().toString(),
        name: action.payload.name,
        targetAmount: action.payload.targetAmount,
        currencyId: action.payload.currencyId,
        savedAmount: 0,
        createdAt: new Date().toISOString(),
      };
      return {
        ...state,
        characters: state.characters.map(char =>
          char.id === action.payload.characterId
            ? { ...char, goals: [...char.goals, goal] }
            : char
        ),
      };
    }
    
    case 'UPDATE_GOAL': {
      return {
        ...state,
        characters: state.characters.map(char =>
          char.id === action.payload.characterId
            ? {
                ...char,
                goals: char.goals.map(goal =>
                  goal.id === action.payload.goalId
                    ? { ...goal, ...action.payload.updates }
                    : goal
                ),
              }
            : char
        ),
      };
    }
    
    case 'DELETE_GOAL': {
      return {
        ...state,
        characters: state.characters.map(char =>
          char.id === action.payload.characterId
            ? { ...char, goals: char.goals.filter(g => g.id !== action.payload.goalId) }
            : char
        ),
      };
    }
    
    case 'DEPOSIT_TO_GOAL': {
      const { characterId, goalId, amount, currencyId } = action.payload;
      return {
        ...state,
        characters: state.characters.map(char => {
          if (char.id !== characterId) return char;
          
          return {
            ...char,
            goals: char.goals.map(goal =>
              goal.id === goalId
                ? { ...goal, savedAmount: (goal.savedAmount || 0) + amount }
                : goal
            ),
          };
        }),
      };
    }
    
    case 'WITHDRAW_FROM_GOAL': {
      const { characterId, goalId, amount } = action.payload;
      return {
        ...state,
        characters: state.characters.map(char => {
          if (char.id !== characterId) return char;
          
          return {
            ...char,
            goals: char.goals.map(goal =>
              goal.id === goalId
                ? { ...goal, savedAmount: Math.max(0, (goal.savedAmount || 0) - amount) }
                : goal
            ),
          };
        }),
      };
    }
    
    case 'RESTORE_DATA':
      return { ...initialState, ...action.payload, currentView: 'home' };
    
    default:
      return state;
  }
};

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState, loadFromStorage);
  
  useEffect(() => {
    saveToStorage(state);
  }, [state]);
  
  useEffect(() => {
    if (state.theme === 'custom' && state.customTheme) {
      // Apply custom theme via inline styles
      const root = document.documentElement;
      root.setAttribute('data-theme', 'custom');
      root.style.setProperty('--background', state.customTheme.background);
      root.style.setProperty('--surface', state.customTheme.surface);
      root.style.setProperty('--surface-hover', state.customTheme.surfaceHover);
      root.style.setProperty('--text', state.customTheme.text);
      root.style.setProperty('--text-muted', state.customTheme.textMuted);
      root.style.setProperty('--primary', state.customTheme.primary);
      root.style.setProperty('--primary-hover', state.customTheme.primaryHover);
      root.style.setProperty('--border', state.customTheme.border);
      root.style.setProperty('--success', state.customTheme.success);
      root.style.setProperty('--danger', state.customTheme.danger);
      root.style.setProperty('--gold', state.customTheme.gold);
    } else {
      // Remove inline styles and use data-theme
      const root = document.documentElement;
      root.setAttribute('data-theme', state.theme);
      root.style.removeProperty('--background');
      root.style.removeProperty('--surface');
      root.style.removeProperty('--surface-hover');
      root.style.removeProperty('--text');
      root.style.removeProperty('--text-muted');
      root.style.removeProperty('--primary');
      root.style.removeProperty('--primary-hover');
      root.style.removeProperty('--border');
      root.style.removeProperty('--success');
      root.style.removeProperty('--danger');
      root.style.removeProperty('--gold');
    }
  }, [state.theme, state.customTheme]);
  
  const selectedCharacter = state.characters.find(c => c.id === state.selectedCharacterId);
  
  const exportBackup = () => {
    const { currentView, ...dataToExport } = state;
    const data = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mywallets.json';
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const importBackup = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          if (data.characters && Array.isArray(data.characters)) {
            dispatch({ type: 'RESTORE_DATA', payload: data });
            resolve(true);
          } else {
            reject(new Error('Invalid backup file'));
          }
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsText(file);
    });
  };
  
  return (
    <AppContext.Provider value={{
      state,
      dispatch,
      selectedCharacter,
      exportBackup,
      importBackup,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
